import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { hasUnsavedCapture, setUnsavedCapture } from './capture/shared/unsavedWork';
import { Task, MediaType } from '../../types';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAppData } from '../../contexts/AppDataContext';
import RoleSwitcher from '../RoleSwitcher';
import { AuthFlow, AuthStep, AuthProfileData } from './auth';
import { WaitingScreen } from './screens/WaitingScreen';
import { UnprovisionedScreen } from './screens/UnprovisionedScreen';
import { TaskList } from './tasks';
import type { TaskListHandle } from './tasks';
import { ProfileSheet } from './profile';
import { isTaskVisibleToParticipant } from '../../utils/taskVisibility';
import { AudioCapture } from './capture/audio';
import { PhotoCapture } from './capture/photo';
import { VideoCapture } from './capture/video';
import { TextCapture } from './capture/text';
import { ScreenCapture } from './capture/screen';
import { GuidedCapture } from './capture/guided';
import { SignificationCapture } from './capture/signification';
import { submitSignificationResponse } from '../../services/participantSubmissionService';
import { FormatSheet, UploadBar } from './shared';
// SubmissionsFeed removed — submissions now render inline in TaskList drawer
import { ParticipantInbox } from './inbox';
import { GuidedHomeShell, GuidedHeaderBand } from './guided';
import { VoiceGuidedShell, playEarcon } from './voice';
import { allowedMediaTypesForUiMode, VOICE_GUIDED_ENABLED } from '../../utils/voiceGuided';
import { useParticipantThreads } from '../../hooks/useParticipantThreads';
import { useInboxThreads } from '../../hooks/useInboxThreads';
import { CaptureErrorBoundary } from './errors';
import { doc, collection } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import { deriveParticipantScreen, isDataReady } from '../../hooks/participantScreen';
import { savePendingUpload } from '../../services/offlineSyncService';
import { getUploadManager } from '../../services/uploadManager';
import { useUploadManager } from '../../contexts/UploadManagerContext';
import { deriveTaskStates } from './tasks/deriveTaskStates';
import { generateThumbnail } from '../../services/thumbnailService';
import { AUDIO } from '../../utils/audioFeedback';
import { safeLocalStorage } from '../../utils/safeStorage';
import { uploadProfilePhoto } from '../../services/storageService';
import { updateUserProfile } from '../../services/authService';
import { markNudgeAsRead } from '../../services/firestoreService';
import {
  saveDraft,
  getDraft,
  deleteDraft,
  getAllDraftTaskIds,
  clearStaleDrafts,
  type DraftOutput,
} from '../../services/draftStorageService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import '../participant/screens/molecules.css';

/* ── Branded loading shell — animated molecular splash ── */
const LOADING_SETS = [
  ['Loading your experience', 'Tuning in', 'Sharpening the lens', 'Listening closely', 'Gathering perspectives', 'Tracing the details', 'Reading between the lines', 'Almost there'],
  ['Focusing the view', 'Catching every nuance', 'Sensing the patterns', 'Piecing it together', 'Noticing the subtle', 'One moment more'],
  ['Still with you', 'Refining the picture', 'Taking it all in', 'Just about ready'],
];

const MOLECULES: React.FC[] = [
  // Constellation (4 nodes)
  () => (
    <svg width="65" height="60" viewBox="-55 -45 170 150" overflow="visible">
      <circle cx="20" cy="16" r="5" fill="#fff"/><circle cx="50" cy="8" r="3" fill="#fff"/><circle cx="65" cy="32" r="4" fill="#fff"/><circle cx="35" cy="50" r="3" fill="#fff"/>
      <line x1="20" y1="16" x2="50" y2="8" stroke="#fff" strokeWidth="2"/><line x1="50" y1="8" x2="65" y2="32" stroke="#fff" strokeWidth="2"/><line x1="20" y1="16" x2="35" y2="50" stroke="#fff" strokeWidth="2"/>
      <path d="M16,12 Q6,2 -4,12 Q-14,2 -24,12 Q-34,2 -44,12" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M68,35 Q78,45 88,35 Q98,45 108,35" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // Diamond (4 nodes)
  () => (
    <svg width="50" height="60" viewBox="-15 -50 85 160" overflow="visible">
      <circle cx="25" cy="5" r="4" fill="#fff"/><circle cx="8" cy="30" r="3" fill="#fff"/><circle cx="42" cy="30" r="3" fill="#fff"/><circle cx="25" cy="55" r="4" fill="#fff"/>
      <line x1="25" y1="5" x2="8" y2="30" stroke="#fff" strokeWidth="2"/><line x1="25" y1="5" x2="42" y2="30" stroke="#fff" strokeWidth="2"/><line x1="8" y1="30" x2="25" y2="55" stroke="#fff" strokeWidth="2"/><line x1="42" y1="30" x2="25" y2="55" stroke="#fff" strokeWidth="2"/>
      <path d="M25,1 Q35,-9 25,-19 Q35,-29 25,-39" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M25,59 Q15,69 25,79 Q15,89 25,99" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // V-shape (3 nodes)
  () => (
    <svg width="55" height="55" viewBox="-45 -15 145 120" overflow="visible">
      <circle cx="8" cy="8" r="4" fill="#fff"/><circle cx="62" cy="8" r="4" fill="#fff"/><circle cx="35" cy="52" r="5" fill="#fff"/>
      <line x1="8" y1="8" x2="35" y2="52" stroke="#fff" strokeWidth="2"/><line x1="62" y1="8" x2="35" y2="52" stroke="#fff" strokeWidth="2"/>
      <path d="M4,8 Q-6,-2 -16,8 Q-26,-2 -36,8" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M35,57 Q45,67 35,77 Q45,87 35,97" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // Triangle (3 nodes)
  () => (
    <svg width="55" height="55" viewBox="-15 -35 140 120" overflow="visible">
      <circle cx="35" cy="5" r="4" fill="#fff"/><circle cx="10" cy="45" r="3" fill="#fff"/><circle cx="60" cy="45" r="3" fill="#fff"/>
      <line x1="35" y1="5" x2="10" y2="45" stroke="#fff" strokeWidth="2"/><line x1="35" y1="5" x2="60" y2="45" stroke="#fff" strokeWidth="2"/><line x1="10" y1="45" x2="60" y2="45" stroke="#fff" strokeWidth="2"/>
      <path d="M35,1 Q25,-9 15,1 Q5,-9 -5,1" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M63,48 Q73,58 83,48 Q93,58 103,48" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // Horizontal pair (2 nodes)
  () => (
    <svg width="60" height="40" viewBox="-55 -35 185 110" overflow="visible">
      <circle cx="15" cy="20" r="5" fill="#fff"/><circle cx="60" cy="20" r="5" fill="#fff"/>
      <line x1="15" y1="20" x2="60" y2="20" stroke="#fff" strokeWidth="2"/>
      <path d="M11,20 Q1,30 -9,20 Q-19,30 -29,20 Q-39,30 -49,20" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M64,20 Q74,10 84,20 Q94,10 104,20" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // Zigzag (3 nodes)
  () => (
    <svg width="60" height="55" viewBox="-40 -25 155 120" overflow="visible">
      <circle cx="10" cy="10" r="3" fill="#fff"/><circle cx="40" cy="40" r="4" fill="#fff"/><circle cx="70" cy="10" r="3" fill="#fff"/>
      <line x1="10" y1="10" x2="40" y2="40" stroke="#fff" strokeWidth="2"/><line x1="40" y1="40" x2="70" y2="10" stroke="#fff" strokeWidth="2"/>
      <path d="M7,10 Q-3,0 -13,10 Q-23,0 -33,10" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M40,44 Q50,54 40,64 Q50,74 40,84" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // Y-shape (4 nodes)
  () => (
    <svg width="50" height="60" viewBox="-40 -25 120 140" overflow="visible">
      <circle cx="10" cy="8" r="3" fill="#fff"/><circle cx="50" cy="8" r="3" fill="#fff"/><circle cx="30" cy="32" r="5" fill="#fff"/><circle cx="30" cy="62" r="3" fill="#fff"/>
      <line x1="10" y1="8" x2="30" y2="32" stroke="#fff" strokeWidth="2"/><line x1="50" y1="8" x2="30" y2="32" stroke="#fff" strokeWidth="2"/><line x1="30" y1="32" x2="30" y2="62" stroke="#fff" strokeWidth="2"/>
      <path d="M7,8 Q-3,-2 -13,8 Q-23,-2 -33,8" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M30,65 Q40,75 30,85 Q40,95 30,105" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  // L-shape (3 nodes)
  () => (
    <svg width="50" height="50" viewBox="-30 -35 135 120" overflow="visible">
      <circle cx="15" cy="5" r="4" fill="#fff"/><circle cx="15" cy="42" r="4" fill="#fff"/><circle cx="55" cy="42" r="3" fill="#fff"/>
      <line x1="15" y1="5" x2="15" y2="42" stroke="#fff" strokeWidth="2"/><line x1="15" y1="42" x2="55" y2="42" stroke="#fff" strokeWidth="2"/>
      <path d="M15,1 Q5,-9 -5,1 Q-15,-9 -25,1" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M58,42 Q68,52 78,42 Q88,52 98,42" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
];

const LoadingShell: React.FC = () => {
  const [setIdx, setSetIdx] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [molIdx, setMolIdx] = useState(0);
  const [molOut, setMolOut] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIdx(prev => {
          const currentSet = LOADING_SETS[setIdx];
          const next = prev + 1;
          if (next < currentSet.length) return next;
          if (setIdx < LOADING_SETS.length - 1) {
            setSetIdx(s => s + 1);
            return 0;
          }
          return 0;
        });
        setVisible(true);
      }, 200);
    }, 2500);
    return () => clearInterval(id);
  }, [setIdx]);

  useEffect(() => {
    const id = setInterval(() => {
      setMolOut(true);
      setTimeout(() => {
        setMolIdx(prev => (prev + 1) % MOLECULES.length);
        setMolOut(false);
      }, 350);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  return (
  <div className="pnp-fullbleed" style={{ background: '#D4715A', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
    <style>{`
      @keyframes lsNodeIn { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
      @keyframes lsLineDraw { 0% { stroke-dashoffset: 80; opacity: 0; } 15% { opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 1; } }
      @keyframes lsWaveDraw { 0% { stroke-dashoffset: 120; opacity: 0; } 10% { opacity: 1; } 100% { stroke-dashoffset: 0; opacity: 1; } }
      @keyframes lsFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
      @keyframes lsShimmer { 0% { opacity: 0.5; } 50% { opacity: 1; } 100% { opacity: 0.5; } }
      @keyframes lsMolIn { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
      @keyframes lsMolOut { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-6px); } }
      .ls-dots span { animation: lsShimmer 1.4s ease-in-out infinite; }
      .ls-mol-in { animation: lsMolIn 1.5s ease-in-out forwards; }
      .ls-mol-in > * { animation: lsFloat 3s ease-in-out infinite; }
      .ls-mol-out { animation: lsMolOut 1s ease-in-out forwards; }
      .ls-dots span:nth-child(2) { animation-delay: 0.2s; }
      .ls-dots span:nth-child(3) { animation-delay: 0.4s; }
    `}</style>

    {/* Branding stack — molecule, Dekko, dots, text — equal 16px gaps */}
    <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <div style={{ pointerEvents: 'none', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div key={molIdx} className={molOut ? 'ls-mol-out' : 'ls-mol-in'}>
          <span style={{display:'block'}}>{React.createElement(MOLECULES[molIdx])}</span>
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: '0.5px' }}>Dekko</div>
      <div className="ls-dots" style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, background: '#fff', borderRadius: '50%', display: 'block' }}/>
        <span style={{ width: 6, height: 6, background: '#fff', borderRadius: '50%', display: 'block' }}/>
        <span style={{ width: 6, height: 6, background: '#fff', borderRadius: '50%', display: 'block' }}/>
      </div>
      <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', opacity: visible ? 1 : 0, transition: 'opacity 0.2s ease' }}>
        {LOADING_SETS[setIdx]?.[msgIdx] ?? ''}
      </div>
    </div>
    <div style={{
      position: 'absolute',
      bottom: 'calc(55px + env(safe-area-inset-bottom, 0px))',
      left: '50%',
      transform: 'translateX(-50%)',
    }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.5px' }}>By Quicksand</span>
    </div>
  </div>
  );
};
import type { InitialDraft } from './capture/shared';
import type { PhotoAnnotation } from '../../types/photo.types';
import './ParticipantNewPage.css';
import './ParticipantNewPage.glass.css';

/**
 * ParticipantNewPage — test harness for the redesigned participant UI.
 *
 * Renders the full auth → task-list → capture flow using only the
 * new component library.  Accessible at /participant-new for testing.
 * No old code is touched or removed.
 */

type PageState =
  | { screen: 'auth' }
  | { screen: 'tasks' }
  | { screen: 'capture'; task: Task; captureType: string; initialDraft?: InitialDraft }
  /** Guided (one_time) home shell. Replaces 'tasks' for studies whose
      Project.taskFrequency === 'one_time'. `step` selects which
      ceremony screen renders. 'brief' is the per-task introduction
      that sits between welcome/rest and the capture flow. */
  | { screen: 'guided'; step: 'welcome' | 'brief' | 'rest' | 'complete' };

export const ParticipantNewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading, logout, refreshClaims, claimsRole, claimsOrgId,
          authResolved, claimsKnown, profileSettled } = useAuthContext();
  const { appData, isLoadingData, currentProjectId: adminProjectId } = useAppData();
  const { jobs: uploadJobs } = useUploadManager();
  // Tasks whose upload exhausted all retries (status 'failed' — set only
  // after MAX_RETRIES, NOT during the retry backoff). Used to stop the
  // optimistic 'just-completed' state from lying when the upload never
  // landed (no Firestore submission was created).
  const failedTaskIds = useMemo(
    () => new Set(uploadJobs.filter(j => j.status === 'failed').map(j => j.taskId)),
    [uploadJobs],
  );
  // Tasks with an upload still in flight — including one recovered from
  // IndexedDB after the browser was closed mid-upload. The submission doc is
  // only written when the upload finishes, so until then these must read as
  // 'pending', never 'not started'.
  const uploadingTaskIds = useMemo(
    () => new Set(
      uploadJobs
        .filter(j => j.status !== 'done' && j.status !== 'failed')
        .map(j => j.taskId),
    ),
    [uploadJobs],
  );
  const isAdminPreview = claimsRole === 'researcher' || claimsRole === 'orgadmin';
  const [page, setPage] = useState<PageState>({ screen: 'auth' });
  // True once a profile-complete participant's assigned project has failed to
  // resolve into appData.projects for long enough that it's terminal (org
  // mismatch / inaccessible project), not just the transient WARM-before-COLD
  // window. Bounds the `!currentProject` loading gate so it can't spin forever.
  const [projectResolveTimedOut, setProjectResolveTimedOut] = useState(false);
  const captureWrapRef = useRef<HTMLDivElement>(null);
  /** Task whose draft is being opened — blocks repeat Resume taps and drives
      the button's loading state. */
  const [resumingTaskId, setResumingTaskId] = useState<string | null>(null);
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);
  const [profileData, setProfileData] = useState<AuthProfileData | undefined>();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const avatarUrlRef = useRef<string | null>(null);

  // Task selection state — which task card is currently shown
  const [activeTaskId, setActiveTaskId] = useState<string | undefined>();

  const [formatSelectTask, setFormatSelectTask] = useState<Task | null>(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [scrollToSubmissionId, setScrollToSubmissionId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'carousel' | 'grid'>('carousel');
  const taskListRef = useRef<TaskListHandle>(null);

  // Submission lineage — set before entering capture for redo/addendum
  const lineageRef = useRef<{
    parentSubmissionId: string;
    submissionType: 'redo' | 'addendum';
    attemptNumber: number;
  } | null>(null);

  // Recently submitted task — drives the just-completed card sequence
  const [recentlySubmittedTaskId, setRecentlySubmittedTaskId] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<Date | undefined>();

  // Draft persistence — IndexedDB-backed
  const [draftTaskIds, setDraftTaskIds] = useState<Set<string>>(new Set());


  // Derive real project/participant/tasks from Firestore data
  // Admin preview: use the project they're currently viewing in the dashboard
  const projectId = useMemo(() => {
    if (isAdminPreview) return adminProjectId || '';
    if (userProfile?.projectId) return userProfile.projectId;
    // Fallback: find participant record for this user (id format: {uid}-{projectId})
    if (user && appData) {
      const prefix = `${user.uid}-`;
      const record = appData.participants.find(p => p.id.startsWith(prefix) && p.projectId);
      if (record) return record.projectId;
    }
    return '';
  }, [isAdminPreview, adminProjectId, userProfile?.projectId, user, appData]);
  const participantId = user && projectId ? `${user.uid}-${projectId}` : '';
  const currentProject = appData?.projects.find(p => p.id === projectId) || null;
  // Resolve orgId with fallback chain: project → claims → userProfile
  const orgId = currentProject?.organizationId || claimsOrgId || userProfile?.organizationId || null;
  /** Guided one_time mode: admin set taskFrequency to 'one_time'. Lands
      the participant in the ceremony shell (welcome → tasks with rest
      screens between → session-complete) instead of the carousel/grid
      home. See docs/superpowers/specs/2026-04-29-study-mode-home-shells. */
  const isGuidedMode = currentProject?.taskFrequency === 'one_time';
  /** Voice-guided (low-literacy) skin on top of guided one_time mode —
      swaps GuidedHomeShell's task screen for VoiceGuidedShell. */
  const isVoiceGuided = VOICE_GUIDED_ENABLED && isGuidedMode && currentProject?.participantUiMode === 'voice_guided';

  /* Declared before `tasks` because the visibility filter needs it: a task the
     participant has already submitted against stays visible even if they are
     later unassigned from it. */
  const submittedTaskIds = useMemo(() => {
    if (!appData || !participantId) return new Set<string>();
    return new Set(
      appData.submissions
        .filter(s => s.participantId === participantId)
        .map(s => s.taskId)
        .filter(Boolean)
    );
  }, [appData, participantId]);

  const tasks = useMemo(() => {
    if (!appData || !projectId) return [];
    return appData.tasks.filter(t =>
      t.projectId === projectId
      && isTaskVisibleToParticipant(t, participantId, { submittedTaskIds, isAdminPreview })
    );
  }, [appData, projectId, participantId, submittedTaskIds, isAdminPreview]);

  const completedCount = useMemo(() => {
    return tasks.filter(t => submittedTaskIds.has(t.id)).length;
  }, [submittedTaskIds, tasks]);

  const userSubmissions = useMemo(() => {
    if (!appData || !participantId) return [];
    return appData.submissions.filter(s => s.participantId === participantId);
  }, [appData, participantId]);

  // Thread messages for all submissions — single Firestore listener
  const { bySubmission: threadsBySubmission, unreadCounts: threadUnreadCounts } =
    useParticipantThreads(participantId || null, projectId || null, orgId);

  // Inbox thread summaries — separate listener for the threads collection
  const { threads: inboxThreads, totalUnread: inboxTotalUnread } =
    useInboxThreads(participantId || null, orgId);

  // Detect redo-requested submissions — latest submission per task that has redoRequestedAt
  // but no newer submission fulfilling it.
  const redoRequestsByTask = useMemo(() => {
    if (!appData || !participantId) return new Map<string, { submissionId: string; reason: string; attemptNumber: number }>();
    const result = new Map<string, { submissionId: string; reason: string; attemptNumber: number }>();
    // Group submissions by taskId
    const byTask = new Map<string, typeof appData.submissions>();
    for (const s of appData.submissions) {
      if (s.participantId !== participantId) continue;
      const taskId = s.taskId;
      if (!taskId) continue;
      const list = byTask.get(taskId) || [];
      list.push(s);
      byTask.set(taskId, list);
    }
    for (const [taskId, subs] of byTask) {
      // Sort by createdAt descending
      const sorted = [...subs].sort((a, b) => {
        const ta = new Date(a.timestamp || a.createdAt || 0).getTime();
        const tb = new Date(b.timestamp || b.createdAt || 0).getTime();
        return tb - ta;
      });
      // Latest submission has redo requested → needs redo
      const latest = sorted[0];
      if (latest?.redoRequestedAt) {
        // Find the redo_request thread message for the reason
        const threadMsgs = threadsBySubmission?.get(latest.id);
        const redoMsg = threadMsgs?.find(m => m.messageType === 'redo_request');
        result.set(taskId, {
          submissionId: latest.id,
          reason: redoMsg?.body || 'Redo requested by researcher',
          attemptNumber: (latest.attemptNumber || 1),
        });
      }
    }
    return result;
  }, [appData, participantId, threadsBySubmission]);

  const taskStates = useMemo(() => deriveTaskStates({
    tasks, submittedTaskIds, draftTaskIds, uploadingTaskIds, failedTaskIds,
    recentlySubmittedTaskId, redoRequestsByTask,
  }), [submittedTaskIds, tasks, draftTaskIds, uploadingTaskIds, recentlySubmittedTaskId, redoRequestsByTask, failedTaskIds]);

  /* If the just-submitted task's upload exhausted all retries, drop the
     optimistic completion so the grid/carousel stop showing it done. The
     task reverts to actionable and the UploadBar surfaces the failure +
     Retry. Without this, grid view pins it 'just-completed' forever
     (the celebration sequence that clears recentlySubmittedTaskId only
     runs in the carousel card path). */
  useEffect(() => {
    if (recentlySubmittedTaskId && failedTaskIds.has(recentlySubmittedTaskId)) {
      setRecentlySubmittedTaskId(null);
      setSubmittedAt(undefined);
    }
  }, [recentlySubmittedTaskId, failedTaskIds]);

  /* Cancelled uploads are demoted back to drafts by the upload manager.
     Fires after the draft is written, so marking the task 'resume' here is
     safe. Also drops the optimistic 'just-completed' state — otherwise the
     task stays pinned as done with no submission behind it. */
  useEffect(() => {
    return getUploadManager().onCancelled((taskId) => {
      setDraftTaskIds(prev => new Set([...prev, taskId]));
      if (recentlySubmittedTaskId === taskId) {
        setRecentlySubmittedTaskId(null);
        setSubmittedAt(undefined);
      }
    });
  }, [recentlySubmittedTaskId]);

  const threadContext = useMemo(() => {
    if (!user || !orgId) return undefined;
    return {
      currentUserUid: user.uid,
      currentUserName: userProfile?.displayName || user.displayName || 'Participant',
      organizationId: orgId!,
    };
  }, [user, userProfile?.displayName, orgId]);

  // Unread nudges for current participant — most recent first.
  // Feeds the hero pill (latestNudge), the inbox sheet's "From your
  // researcher" section, AND the dock unread badge total.
  const unreadNudgesForMe = useMemo(() => {
    if (!appData || !participantId) return [];
    return appData.nudges
      .filter(n => n.participantId === participantId && !n.isRead)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [appData, participantId]);
  const latestNudge = unreadNudgesForMe[0] || null;

  /** Recently-read nudges (within the last 7 days). Rendered below the
      unread section in the inbox so participants can re-read messages
      they've already acknowledged. Older than 7 days drops off. */
  const recentReadNudgesForMe = useMemo(() => {
    if (!appData || !participantId) return [];
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return appData.nudges
      .filter(n => n.participantId === participantId && n.isRead && new Date(n.sentAt).getTime() >= cutoff)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [appData, participantId]);

  // Smart action bar counts — prefer inbox thread counters when available
  // and add unread nudges so the dock badge reflects all unread comms.
  const totalUnreadCount = useMemo(() => {
    const threadsUnread = inboxTotalUnread > 0
      ? inboxTotalUnread
      : (threadUnreadCounts
        ? Array.from(threadUnreadCounts.values()).reduce((s, n) => s + n, 0)
        : 0);
    return threadsUnread + unreadNudgesForMe.length;
  }, [threadUnreadCounts, inboxTotalUnread, unreadNudgesForMe.length]);

  const redoCount = useMemo(() => redoRequestsByTask.size, [redoRequestsByTask]);

  // Milestone message for the just-completed card
  const milestone = useMemo(() => {
    if (!recentlySubmittedTaskId) return undefined;
    const doneCount = submittedTaskIds.has(recentlySubmittedTaskId)
      ? completedCount
      : completedCount + 1;
    const total = tasks.length;
    if (total === 0) return undefined;
    if (doneCount === total) return 'All done!';
    if (doneCount === 1) return 'Great start!';
    if (total > 2 && doneCount === Math.ceil(total / 2)) return 'Halfway there!';
    if (total > 2 && doneCount === total - 1) return 'Almost there!';
    return undefined;
  }, [recentlySubmittedTaskId, completedCount, tasks.length, submittedTaskIds]);

  // Streak — consecutive days with at least one submission
  const streak = useMemo(() => {
    if (userSubmissions.length === 0) return 0;
    const days = new Set(
      userSubmissions.map(s => {
        const d = (s.createdAt as any)?.toDate ? (s.createdAt as any).toDate() : new Date(s.timestamp || s.createdAt);
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      })
    );
    let count = 0;
    const now = new Date();
    for (let i = 0; i < 365; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (days.has(key)) {
        count++;
      } else if (i === 0) {
        continue; // today might have no submissions yet — check from yesterday
      } else {
        break;
      }
    }
    return count;
  }, [userSubmissions]);

  // Study timeline — "Day X of Y" when project has start/end dates
  const studyTimeline = useMemo(() => {
    if (!currentProject?.startDate || !currentProject?.endDate) return null;
    const start = new Date(currentProject.startDate).getTime();
    const end = new Date(currentProject.endDate).getTime();
    const now = Date.now();
    if (isNaN(start) || isNaN(end) || end <= start) return null;
    const dayNumber = Math.max(1, Math.floor((now - start) / 86400000) + 1);
    const totalDays = Math.ceil((end - start) / 86400000);
    const progress = Math.min(1, Math.max(0, (now - start) / (end - start)));
    if (dayNumber > totalDays) return null; // study ended
    return { dayNumber, totalDays, progress };
  }, [currentProject?.startDate, currentProject?.endDate]);

  // Researcher presence — find the most recently active team member for this project
  const researcherPresence = useMemo(() => {
    if (!appData || !projectId) return null;
    const members = appData.teamMembers.filter(m => m.projectId === projectId && m.lastActiveAt);
    if (members.length === 0) return null;
    const best = members.reduce((a, b) => {
      const aTime = a.lastActiveAt?.toDate ? a.lastActiveAt.toDate().getTime() : new Date(a.lastActiveAt).getTime();
      const bTime = b.lastActiveAt?.toDate ? b.lastActiveAt.toDate().getTime() : new Date(b.lastActiveAt).getTime();
      return bTime > aTime ? b : a;
    });
    const lastTime = best.lastActiveAt?.toDate ? best.lastActiveAt.toDate().getTime() : new Date(best.lastActiveAt).getTime();
    const hoursAgo = (Date.now() - lastTime) / 3600000;
    if (hoursAgo > 24) return null;
    return {
      name: best.name?.split(' ')[0] || 'Researcher',
      recent: hoursAgo < 2,
    };
  }, [appData, projectId]);


  // Resolve sender name from team members. Three id shapes to handle:
  //   - `userId === sentBy` (modern docs written by joinProject cloud fn)
  //   - `id === sentBy` (very old docs where the doc id IS the auth uid)
  //   - `id.startsWith(sentBy + '-')` (composite `${uid}-${projectId}` docs
  //     that pre-date the userId field — most "legacy" researcher accounts
  //     fall in this bucket)
  // If nothing matches we still want the hero pill to show, so we return
  // a sensible default instead of undefined.
  const nudgeSenderName = useMemo(() => {
    if (!latestNudge || !appData) return undefined;
    const sentBy = latestNudge.sentBy;
    const member = appData.teamMembers.find(m =>
      m.userId === sentBy ||
      m.id === sentBy ||
      m.id.startsWith(`${sentBy}-`)
    );
    return member?.name;
  }, [latestNudge, appData]);

  // Resolve task name for nudge deep-link
  const nudgeTaskName = useMemo(() => {
    if (!latestNudge?.taskId) return undefined;
    const task = tasks.find(t => t.id === latestNudge.taskId);
    return task?.title || task?.prompt;
  }, [latestNudge, tasks]);

  /** Drives the hero pill — visible as long as there's an unread nudge in
      Firestore. Persistent until the user explicitly acknowledges via the
      inbox (which flips Firestore isRead). */
  const activeHeroNudge = latestNudge;

  /** Ids the participant has already seen the arrival animation for in
      this mount. Used to one-shot the pill bounce only on genuinely new
      nudges, not on refreshes / re-renders / sub-screen navigations. */
  const seenNudgeIdsRef = useRef<Set<string>>(new Set());
  /** When non-null, the pill currently runs its arrival animation for
      this nudge id. Cleared after the animation completes. */
  const [animatingNudgeId, setAnimatingNudgeId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeHeroNudge) return;
    if (seenNudgeIdsRef.current.has(activeHeroNudge.id)) return;
    seenNudgeIdsRef.current.add(activeHeroNudge.id);
    setAnimatingNudgeId(activeHeroNudge.id);
    const t = setTimeout(() => setAnimatingNudgeId(null), 700);
    return () => clearTimeout(t);
  }, [activeHeroNudge?.id]);

  // Navigate to a specific task (used by nudge deep-link)
  const handleNudgeNavigateToTask = useCallback((taskId: string) => {
    setActiveTaskId(taskId);
    if (page.screen !== 'tasks') setPage({ screen: 'tasks' });
  }, [page.screen]);

  /* Submit failures that have no background retry behind them. Rendered as a
     dismissible strip — see statusStrip below. */
  const [submitError, setSubmitError] = useState<string | null>(null);

  /* Offline indicator. The researcher shell has had a banner for a while
     (App.tsx), but /p replaces that chrome entirely, so participants had no
     connectivity signal at all unless an upload happened to be in flight. */
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  /* Load draft task IDs from IndexedDB on mount + clean stale drafts */
  useEffect(() => {
    getAllDraftTaskIds()
      .then(ids => setDraftTaskIds(new Set(ids)))
      .catch(() => {}); // IndexedDB unavailable (e.g. iOS private browsing)
    clearStaleDrafts(7).catch(() => {});
  }, []);


  /* Dev helper: /participant-new?reset-onboarding clears profileComplete so onboarding reruns */
  const isResettingOnboarding = new URLSearchParams(window.location.search).has('reset-onboarding');
  useEffect(() => {
    if (isResettingOnboarding && user?.uid) {
      updateUserProfile(user.uid, { profileComplete: false } as any).then(() => {
        window.location.replace('/participant-new');
      });
    }
  }, [user?.uid, isResettingOnboarding]);

  /* Skip auth screen if user is already authenticated + profile complete + has a project with tasks */
  const forceOnboarding = new URLSearchParams(window.location.search).has('onboarding');
  useEffect(() => {
    if (forceOnboarding) return; // ?onboarding query param — always show onboarding

    // Clear stale pendingProjectJoin if user already belongs to that project
    const pendingJoin = safeLocalStorage.getItem('pendingProjectJoin');
    if (pendingJoin) {
      if (projectId === pendingJoin) {
        safeLocalStorage.removeItem('pendingProjectJoin');
        safeLocalStorage.removeItem('pendingJoinRole');
      } else {
        return; // genuinely pending join — let onboarding run
      }
    }

    if (forceOnboarding) return;
    // Admin preview: researchers/orgadmins skip straight to tasks
    if (!loading && user && isAdminPreview && page.screen === 'auth') {
      setPage({ screen: 'tasks' });
      return;
    }
    // Wait for currentProject to resolve before committing to a home
    // layout. projects load COLD (getDocs) while tasks load WARM
    // (onSnapshot), so tasks.length can be > 0 while currentProject is
    // still null → isGuidedMode would be false and we'd land on the
    // daily carousel, then flip to the guided shell once projects arrive
    // (the one_time → daily-card flash). Gating on currentProject makes
    // the guided-vs-tasks branch correct on the first transition.
    // `|| projectResolveTimedOut`: if the cold projects fetch failed terminally
    // (currentProject never resolves) but tasks arrived, proceed to the home
    // shell anyway — otherwise the auth-screen gate holds LoadingShell forever
    // (its tasks.length arm never yields). Guided-vs-tasks may be wrong until
    // the project recovers; the reconcile effect below corrects it.
    if (!loading && user && userProfile?.profileComplete && projectId && (currentProject || projectResolveTimedOut) && tasks.length > 0 && page.screen === 'auth') {
      if (isGuidedMode) {
        // Guided mode lands on its own ceremony shell instead of the
        // carousel/grid home. If all tasks are already done (e.g. the
        // participant finished previously and is reopening), go
        // straight to the complete screen.
        const allDone = submittedTaskIds.size >= tasks.length;
        setPage({ screen: 'guided', step: allDone ? 'complete' : 'welcome' });
      } else {
        setPage({ screen: 'tasks' });
      }
    }
  }, [loading, user, userProfile?.profileComplete, projectId, currentProject?.id, projectResolveTimedOut, tasks.length, isAdminPreview, forceOnboarding, isGuidedMode, submittedTaskIds.size]);

  /* Reconcile page state with guided-mode flips that happen AFTER the
     initial auth → home transition. Without this, a participant whose
     project's taskFrequency changes from 'daily' → 'one_time' while
     they're already sitting on the carousel home stays on the carousel
     (and vice versa). Runs on every change to isGuidedMode + home-state
     residency; capture / auth pages are left alone. */
  useEffect(() => {
    if (loading || !user || !tasks.length) return;
    if (page.screen === 'tasks' && isGuidedMode) {
      const allDone = submittedTaskIds.size >= tasks.length;
      setPage({ screen: 'guided', step: allDone ? 'complete' : 'welcome' });
      return;
    }
    if (page.screen === 'guided' && !isGuidedMode) {
      setPage({ screen: 'tasks' });
    }
  }, [isGuidedMode, page.screen, loading, user, tasks.length, submittedTaskIds.size]);

  /* Handle pending project join from /join/:projectId redirect */
  const [joiningProject, setJoiningProject] = useState(false);
  /** Last joinProject attempt threw. Routes to the retryable 'join' screen
      instead of the terminal UnprovisionedScreen, and blocks this effect
      from retry-looping until the user re-engages (see handleAuthComplete,
      which clears it). */
  const [joinFailed, setJoinFailed] = useState(false);
  useEffect(() => {
    const handlePendingJoin = async () => {
      if (!user || loading || joiningProject || joinFailed) return;

      const pendingProjectId = safeLocalStorage.getItem('pendingProjectJoin');
      if (!pendingProjectId) return;

      // Admin/superadmin users should not join as participants
      if (claimsRole === 'researcher' || claimsRole === 'orgadmin' || claimsRole === 'superadmin') {
        safeLocalStorage.removeItem('pendingProjectJoin');
        safeLocalStorage.removeItem('pendingJoinRole');
        return;
      }

      // Already joined this project
      if (projectId === pendingProjectId) {
        safeLocalStorage.removeItem('pendingProjectJoin');
        safeLocalStorage.removeItem('pendingJoinRole');
        return;
      }

      setJoiningProject(true);
      setJoinFailed(false);
      try {
        const functions = getFunctions();
        const joinProjectFn = httpsCallable(functions, 'joinProject');

        await joinProjectFn({ projectId: pendingProjectId });

        // joinProject just stamped new claims and revoked the old token
        // (see the same race documented in firestoreService's cold-fetch
        // retry). The updateDoc below would run against the stale token and
        // be denied by rules — a join failure only a manual reload cured.
        await refreshClaims();

        await updateUserProfile(user.uid, {
          role: 'participant',
          participantId: user.uid,
          projectId: pendingProjectId,
        });

        safeLocalStorage.removeItem('pendingProjectJoin');
        safeLocalStorage.removeItem('pendingJoinRole');
      } catch (err) {
        // Keep pendingProjectJoin so the 'join' screen can retry; never fall
        // through to UnprovisionedScreen, which reads as a rejection.
        console.error('[JOIN:FAIL]', { err });
        setJoinFailed(true);
      } finally {
        setJoiningProject(false);
      }
    };

    handlePendingJoin();
  }, [user, loading, projectId, claimsRole, joinFailed]);

  /* Revoke blob URL on unmount */
  useEffect(() => {
    return () => {
      if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
    };
  }, []);

  /* ── Bound the project-resolution wait ──
     A participant whose assigned projectId never appears in appData.projects
     (org mismatch / stale claims / inaccessible) would otherwise hold
     LoadingShell forever. The transient case (projects COLD-fetching while
     tasks arrived WARM) resolves within the cold-retry budget
     (firestoreService backoff sums to ~5s); past that it's terminal. Arm a 7s
     timer (safely beyond 5s, so a slow-but-succeeding cold fetch isn't cut off
     into a flash-back) and fall through to WaitingScreen when it fires.
     NOT gated on profileComplete: this also feeds `projectSettled` for the
     screen oracle, and a mid-onboarding participant (profile not yet complete)
     whose project never resolves must still reach an honest screen instead of
     an endless loader. */
  useEffect(() => {
    const awaitingProject = !!user && !!projectId &&
      !currentProject && !isLoadingData && !isAdminPreview;
    if (!awaitingProject) {
      if (projectResolveTimedOut) setProjectResolveTimedOut(false);
      return;
    }
    const t = setTimeout(() => setProjectResolveTimedOut(true), 7000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId, currentProject, isLoadingData, isAdminPreview]);

  /* ── Auth complete → show task list ── */
  const handleAuthComplete = (data?: AuthProfileData) => {
    // AuthFlow exposes no per-step callback (see auth/AuthFlow.tsx) — this is
    // the only signal the parent gets that the user walked through the join
    // step again. Clear unconditionally, as a real state transition, so
    // handlePendingJoin's effect (guarded on joinFailed above) gets a fresh
    // attempt. Re-setting it true in the same tick below would collapse to a
    // no-op and never actually unblock the retry — the guard just declines
    // to proceed instead; pendingProjectJoin staying in localStorage is what
    // keeps deriveParticipantScreen routing to 'join' either way.
    setJoinFailed(false);

    // Resolved before the guard below so both branches agree on it: whether
    // profileComplete gets set, not whether the entered data gets kept.
    const hasProject = !!(projectId || userProfile?.projectId);

    if (data) {
      // Keep this unconditional — a join-failure retry means the user is
      // filling this form out again; discarding it here means they'd have
      // to enter it a third time once the retry lands.
      setProfileData(data);
      if (data.avatarBlob) {
        // Show local blob URL immediately
        if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
        const url = URL.createObjectURL(data.avatarBlob);
        avatarUrlRef.current = url;
        setAvatarUrl(url);

        // Fire-and-forget: upload to Firebase Storage + persist URL to
        // Firestore. Both writes are uid-scoped only (storage.rules:52-58,
        // firestore.rules:152-158) — no project/org check — so a missing
        // project can't turn this into a permission error.
        if (user) {
          const file = new File([data.avatarBlob], 'avatar.jpg', { type: data.avatarBlob.type || 'image/jpeg' });
          uploadProfilePhoto(file)
            .then(meta => updateUserProfile(user.uid, { avatarUrl: meta.downloadURL, ...(hasProject ? { profileComplete: true } : {}) }))
            .catch(err => console.warn('[ParticipantNewPage] Avatar upload failed:', err));
        }
      } else if (user && hasProject) {
        // No avatar but profile setup completed — mark profileComplete for pull-to-refresh
        updateUserProfile(user.uid, { profileComplete: true })
          .catch(err => console.warn('[ParticipantNewPage] Profile update failed:', err));
      }
    }

    // A participant with no resolved project is not onboarded — their join
    // never landed. Marking profileComplete here strands them in the app
    // with no projectId; bail out instead so the retry above gets a chance
    // and they land back on the retryable join screen, not deep in the app.
    // Profile data entered above is already preserved, not thrown away.
    if (!hasProject) {
      console.error('[ONBOARD:NO_PROJECT] refusing to mark profile complete');
      return;
    }
    setPage({ screen: 'tasks' });
  };

  /* ── Avatar edit from ProfileSheet ── */
  const handleAvatarChange = (blob: Blob | null) => {
    if (!user) return;

    if (blob === null) {
      // Remove: clear locally + persist empty avatarUrl
      if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
      avatarUrlRef.current = null;
      setAvatarUrl(null);
      updateUserProfile(user.uid, { avatarUrl: '' })
        .catch(err => console.warn('[ParticipantNewPage] Avatar remove failed:', err));
      return;
    }

    // Set: local preview immediately, fire-and-forget upload
    if (avatarUrlRef.current) URL.revokeObjectURL(avatarUrlRef.current);
    const url = URL.createObjectURL(blob);
    avatarUrlRef.current = url;
    setAvatarUrl(url);

    const file = new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' });
    uploadProfilePhoto(file)
      .then(meta => updateUserProfile(user.uid, { avatarUrl: meta.downloadURL, profileComplete: true }))
      .catch(err => console.warn('[ParticipantNewPage] Avatar upload failed:', err));
  };

  /* ── Select a task from navigator — shows the card, does NOT start capture ── */
  const handleSelectTask = (task: Task) => {
    setActiveTaskId(task.id);
  };

  /* ── Start a capture flow ── */
  const handleStartTask = (task: Task) => {
    setActiveTaskId(task.id);
    if (task.mediaTypes.length > 1) {
      setFormatSelectTask(task);
    } else {
      setPage({ screen: 'capture', task, captureType: task.mediaTypes[0] || 'audio' });
    }
  };

  /** Next-incomplete task in display order — drives the guided welcome
      preview chip and the "Begin/Continue/Advance" handler. */
  const nextIncompleteTask = useMemo(() => {
    if (!tasks.length) return null;
    return tasks.find(t => !submittedTaskIds.has(t.id)) || null;
  }, [tasks, submittedTaskIds]);

  /** Voice-guided only: tasks created before the project flipped to
      voice_guided may carry mediaTypes with none of the voice-guided
      allow-list (audio/video/photo) — e.g. a text-only task. The shell
      renders zero answer buttons for those, a dead end. Exclude them here
      so both the task the shell shows AND the dots it counts over
      (total/done below) skip them consistently. */
  const voiceGuidedEligibleTasks = useMemo(() => {
    if (!isVoiceGuided) return tasks;
    const allowed = allowedMediaTypesForUiMode(currentProject?.participantUiMode);
    if (!allowed) return tasks;
    return tasks.filter(t => t.mediaTypes?.some(m => allowed.includes(m)));
  }, [tasks, isVoiceGuided, currentProject?.participantUiMode]);

  /** Voice-guided only: nextIncompleteTask doesn't correct for the same
      Firestore round-trip lag taskStates/milestone already account for
      via recentlySubmittedTaskId (see :393-411, :495-497) — standard
      guided mode masks that lag behind the rest screen's manual tap, but
      voice-guided flips straight back into the shell, so without this it
      would re-ask the question the participant just answered until
      submittedTaskIds catches up. Skip (not null) recentlySubmittedTaskId
      so it advances to the real next task instead of flashing complete. */
  const voiceGuidedTask = useMemo(
    () => voiceGuidedEligibleTasks.find(t => !submittedTaskIds.has(t.id) && t.id !== recentlySubmittedTaskId) || null,
    [voiceGuidedEligibleTasks, submittedTaskIds, recentlySubmittedTaskId]
  );

  /** done/total for the shell's dots — counted over the same eligible
      subset as voiceGuidedTask above, so a skipped legacy task doesn't
      leave the dots one short of what the participant actually answers. */
  const voiceGuidedDone = useMemo(() => {
    const submittedEligible = voiceGuidedEligibleTasks.filter(t => submittedTaskIds.has(t.id)).length;
    return recentlySubmittedTaskId && !submittedTaskIds.has(recentlySubmittedTaskId)
      ? submittedEligible + 1
      : submittedEligible;
  }, [voiceGuidedEligibleTasks, submittedTaskIds, recentlySubmittedTaskId]);

  /** Guided welcome's "Begin/Continue" / rest's tap-advance: move to
      the per-task BRIEF screen (the new ceremonial intro between
      welcome/rest and the actual capture flow). If there are no
      remaining tasks, jump to the complete screen. */
  const handleGuidedAdvance = useCallback(() => {
    if (!nextIncompleteTask) {
      setPage({ screen: 'guided', step: 'complete' });
      return;
    }
    setPage({ screen: 'guided', step: 'brief' });
  }, [nextIncompleteTask]);

  /** Brief's "Start task" — opens the capture screen for the next
      incomplete task. */
  const handleGuidedStartTask = useCallback(() => {
    if (!nextIncompleteTask) return;
    handleStartTask(nextIncompleteTask);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextIncompleteTask]);

  /** Voice-guided answer-screen tap — the participant already picked the
      MediaType by icon, so open capture with it directly instead of
      routing through handleStartTask's format-select sheet. */
  const handleVoiceGuidedAnswer = useCallback((type: MediaType) => {
    // Targets voiceGuidedTask, not nextIncompleteTask — must match whatever
    // task the shell is actually showing (see voiceGuidedTask's comment),
    // or a tap during the sync window would open capture for the wrong task.
    if (!voiceGuidedTask) return;
    setActiveTaskId(voiceGuidedTask.id);
    setPage({ screen: 'capture', task: voiceGuidedTask, captureType: type });
  }, [voiceGuidedTask]);

  /** Brief's back-arrow — returns to the previous ceremony step.
      If the participant hasn't done anything yet, back goes to
      welcome; mid-flow (1+ done) it goes back to rest. */
  const handleGuidedBackFromBrief = useCallback(() => {
    setPage({ screen: 'guided', step: submittedTaskIds.size > 0 ? 'rest' : 'welcome' });
  }, [submittedTaskIds.size]);

  /* ── Addendum: add more to an existing submission ── */
  const handleAddendum = (submission: import('../../types').Submission, task: Task) => {
    lineageRef.current = {
      parentSubmissionId: submission.id,
      submissionType: 'addendum',
      attemptNumber: 1,
    };
    setActiveTaskId(task.id);
    if (task.mediaTypes.length > 1) {
      setFormatSelectTask(task);
    } else {
      setPage({ screen: 'capture', task, captureType: task.mediaTypes[0] || 'audio' });
    }
  };

  /* ── Redo: redo a task requested by researcher ── */
  const handleRedo = (task: Task) => {
    const redo = redoRequestsByTask.get(task.id);
    if (!redo) return;
    lineageRef.current = {
      parentSubmissionId: redo.submissionId,
      submissionType: 'redo',
      attemptNumber: redo.attemptNumber + 1,
    };
    setActiveTaskId(task.id);
    if (task.mediaTypes.length > 1) {
      setFormatSelectTask(task);
    } else {
      setPage({ screen: 'capture', task, captureType: task.mediaTypes[0] || 'audio' });
    }
  };

  const handleFormatSelect = (captureType: string) => {
    if (formatSelectTask) {
      setPage({ screen: 'capture', task: formatSelectTask, captureType });
      setFormatSelectTask(null);
    }
  };

  /* ── Capture done or closed → back to task list, scrolled to the same card ── */
  const [showLeaveNudge, setShowLeaveNudge] = useState(false);

  const performCaptureClose = useCallback(() => {
    setShowLeaveNudge(false);
    if (page.screen === 'capture') setActiveTaskId(page.task.id);
    setPage({ screen: 'tasks' });
  }, [page]);

  const handleCaptureClose = useCallback(() => {
    // Captured media that was never saved would be silently destroyed by
    // leaving — nudge instead of closing. Deliberately a nudge, not an
    // autosave: saving costs an upload on iOS in-app browsers.
    if (page.screen === 'capture' && hasUnsavedCapture()) {
      // A browser-back arrives here having already consumed the history
      // sentinel; we're staying on capture, so restore it.
      if (!window.history.state?.dekkoCapture) {
        window.history.pushState({ ...window.history.state, dekkoCapture: true }, '');
      }
      setShowLeaveNudge(true);
      return;
    }
    performCaptureClose();
  }, [page, performCaptureClose]);

  // iOS's native edge-swipe is itself a browser "back" and cannot be
  // intercepted from JS, so running our own edge gesture there double-fires
  // (ours closes capture, the native one leaves the site — the eject bug).
  // On iOS the native gesture + the history sentinel below IS the back
  // gesture; the custom one stays Android-only.
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  // Swipe-right from left edge to go back from capture → tasks
  useSwipeBack(captureWrapRef, handleCaptureClose, page.screen === 'capture' && !isIOS);

  // History sentinel: while capture is open, a browser back (iOS edge-swipe,
  // Android hardware back) closes the capture screen instead of leaving the
  // site — participants usually arrive from a join link with no history
  // behind the app. Programmatic closes consume the sentinel in cleanup so
  // history stays balanced. Router state is spread in to keep react-router's
  // own bookkeeping intact.
  const captureCloseRef = useRef(handleCaptureClose);
  captureCloseRef.current = handleCaptureClose;
  useEffect(() => {
    if (page.screen !== 'capture') return;
    window.history.pushState({ ...window.history.state, dekkoCapture: true }, '');
    const onPop = () => captureCloseRef.current();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.dekkoCapture) window.history.back();
    };
  }, [page.screen]);

  /* ── Navigate after enqueue — instant transition to celebration card ── */
  const navigateAfterSubmit = useCallback(async (taskId: string) => {
    lineageRef.current = null;
    setRecentlySubmittedTaskId(taskId);
    setSubmittedAt(new Date());
    setActiveTaskId(taskId);
    if (isGuidedMode) {
      // Guided one_time mode: route through rest -> next task instead
      // of the carousel home. submittedTaskIds may not yet include the
      // just-submitted id (Firestore round-trip pending) so we compute
      // the post-submit count optimistically.
      const postSubmitCount = submittedTaskIds.size + (submittedTaskIds.has(taskId) ? 0 : 1);
      const allDone = postSubmitCount >= tasks.length;
      if (isVoiceGuided) playEarcon('success');
      setPage({ screen: 'guided', step: allDone ? 'complete' : 'rest' });
    } else {
      setPage({ screen: 'tasks' });
    }
    // The draft is NOT deleted here. It stays on disk as a second copy until
    // the upload actually succeeds, at which point uploadManager deletes it.
    // Deleting at submit time left the pending-upload record as the only copy
    // of the media for the entire upload — and permanently, if it never ran.
    // Dropping it from draftTaskIds is just optimistic UI (a submitted task
    // never shows 'resume'; see the taskStates memo).
    setDraftTaskIds(prev => { const n = new Set(prev); n.delete(taskId); return n; });
  }, [isGuidedMode, isVoiceGuided, submittedTaskIds, tasks.length]);

  /* ── Submit handler — saves blobs to IndexedDB, enqueues to upload manager, navigates instantly ── */
  const makeSubmitHandler = (task: Task, captureType: string) => async (...args: unknown[]) => {
    const lineage = lineageRef.current;

    // Extract blobs and notes from capture-type-specific args
    let blobs: Blob[] = [];
    let notes = '';
    let photoAnnotations: PhotoAnnotation[] | undefined;
    switch (captureType) {
      case 'audio':
        // Blob[] — one file per recorded clip. Clips are never merged; see
        // AudioCapture.handleSubmit.
        blobs = (args[0] as Blob[]) || [];
        notes = (args[1] as string) || '';
        break;
      case 'photo':
        blobs = (args[0] as Blob[]) || [];
        notes = (args[1] as string) || '';
        photoAnnotations = args[2] as PhotoAnnotation[] | undefined;
        break;
      case 'video':
        blobs = (args[0] as Blob[]) || [];
        notes = (args[1] as string) || '';
        break;
      case 'text':
        notes = (args[0] as string) || '';
        if (args[1]) blobs = [args[1] as File];
        break;
      case 'screen':
        blobs = [args[0] as Blob];
        notes = (args[1] as string) || '';
        break;
      case 'guided':
        blobs = [args[0] as File];
        notes = (args[1] as string) || '';
        break;
      case 'signification': {
        // Signification bypasses blob-based upload manager — writes JSON directly to Firestore
        const significationData = args[0] as import('../../types/signification.types').SignificationSubmission;
        const audioBlobs = (args[1] as Blob[]) || [];
        try {
          await submitSignificationResponse({
            projectId: task.projectId,
            participantId,
            taskId: task.id,
            significationData,
            audioBlobs,
            ...(lineage ? {
              parentSubmissionId: lineage.parentSubmissionId,
              submissionType: lineage.submissionType,
              attemptNumber: lineage.attemptNumber,
            } : {}),
          });
          AUDIO.success();
          navigateAfterSubmit(task.id);
        } catch (err) {
          console.error('[ParticipantNewPage] Signification submit failed:', err);
          // Signification has no background upload queue, so a failure here is
          // terminal for this attempt — say so instead of silently staying put.
          // The responses are still in the capture screen's state (and now on
          // disk with the pending record), so retrying costs nothing.
          setSubmitError("Couldn't submit your responses. Check your connection and try again.");
        }
        return;
      }
    }

    // Generate a submission ID upfront
    const submissionId = doc(collection(db, 'submissions')).id;

    const uploadMeta = {
      submissionId,
      projectId: task.projectId,
      participantId,
      taskId: task.id,
      taskTitle: task.title || task.prompt,
      mediaType: captureType,
      notes,
      ...(lineage ? {
        parentSubmissionId: lineage.parentSubmissionId,
        submissionType: lineage.submissionType,
        attemptNumber: lineage.attemptNumber,
      } : {}),
      ...(photoAnnotations ? { photoAnnotations } : {}),
    };

    // Persist blobs to IndexedDB so the upload survives navigation and restarts.
    // This is durability, NOT a prerequisite for uploading: iOS in-app browsers
    // (WhatsApp/Instagram — WKWebView) accept small IndexedDB records but refuse
    // to store Blobs, which used to fail the whole submission and throw away a
    // capture the participant had already made. Fall back to an in-memory
    // upload instead, and tell them the one thing that would break it.
    let pendingId: number | null = null;
    try {
      pendingId = await savePendingUpload(uploadMeta, blobs);
    } catch (err) {
      console.error('[ParticipantNewPage] Blob persistence unavailable; uploading from memory:', err);
    }

    try {
      // Generate a thumbnail for instant display (non-blocking — null if it fails)
      const thumbnailUrl = blobs.length > 0
        ? await generateThumbnail(blobs[0], captureType).catch(() => null)
        : null;

      const jobParams = {
        ...uploadMeta,
        createdAt: Date.now(),
        parentSubmissionId: lineage?.parentSubmissionId,
        submissionType: lineage?.submissionType,
        attemptNumber: lineage?.attemptNumber,
        thumbnailUrl,
      };

      if (pendingId === null) {
        // Memory-only: lost on reload, so say so rather than promising a
        // background upload we cannot deliver.
        getUploadManager().enqueueInMemory(jobParams, blobs);
        // UploadManagerProvider already raises a leave-page prompt while
        // any upload is in flight. What is specific to THIS case is that the
        // capture isn't saved to the device, so an interruption loses it
        // outright rather than resuming later. Do NOT name a browser to switch to: this same
        // path fires in real Safari (private tab, low storage), where
        // "open in Safari" reads as nonsense.
        setSubmitError("Your recording isn't saved on this device, so keep this screen open until the upload finishes. A private tab or low device storage can cause this.");
      } else {
        getUploadManager().enqueue({ ...jobParams, id: pendingId });
      }

      // Navigate immediately — upload happens in the background
      AUDIO.success();
      navigateAfterSubmit(task.id);
    } catch (err) {
      console.error('[ParticipantNewPage] Failed to enqueue upload:', err);
      setSubmitError("Couldn't start the upload. Check your connection and try again.");
    }
  };

  /* ── Draft handler — saves to IndexedDB with actual blob data ── */
  const makeDraftHandler = (task: Task, captureType: string) => async (...args: unknown[]) => {
    let notes = '';
    let textContent: string | undefined;
    let blobs: Blob[] = [];
    const durations: number[] = [];

    if (captureType === 'text') {
      textContent = (args[0] as string) || '';
      const file = args[1] as File | null;
      if (file) blobs = [file];
    } else if (captureType === 'photo' || captureType === 'video' || captureType === 'audio') {
      // photo, video and audio pass Blob[] as first arg (one entry per
      // photo / take / clip)
      blobs = (args[0] as Blob[]) || [];
      notes = (args[1] as string) || '';
    } else {
      // screen, guided — args[0] is Blob|File|null, args[1] is notes
      const blob = args[0] as Blob | null;
      if (blob) blobs = [blob];
      notes = (args[1] as string) || '';
    }

    try {
      await saveDraft(task.id, { captureType, notes, textContent, blobs, durations, lineage: lineageRef.current || undefined });
      setDraftTaskIds(prev => new Set([...prev, task.id]));
    } catch (err) {
      console.error('[ParticipantNewPage] Draft save failed:', err);
      setSubmitError("Couldn't save your draft — your work is still on screen. Check your connection and try Save draft again, or submit now.");
      return; // stay on the capture screen; do NOT navigate away from their work
    }
    setActiveTaskId(task.id); // scroll carousel to this task so Resume button is visible
    setPage({ screen: 'tasks' });
  };

  /* ── Resume a drafted task — loads from IndexedDB ── */
  const handleResumeTask = async (task: Task) => {
    // Re-entrancy guard: a remote draft's media is re-downloaded on every
    // resume, so an impatient second tap on a slow connection starts a second
    // multi-megabyte transfer. Ignore taps until the first one settles.
    if (resumingTaskId) return;
    setResumingTaskId(task.id);
    try {
      await openDraftForResume(task);
    } finally {
      setResumingTaskId(null);
    }
  };

  const openDraftForResume = async (task: Task) => {
    let draft: DraftOutput | null = null;
    try {
      draft = await getDraft(task.id);
    } catch (err) {
      // A read failure is not a missing draft. Falling through to start fresh
      // would drop the Resume pill and tell the participant their saved work
      // is gone when it is still on the device.
      console.error('[ParticipantNewPage] Draft read failed:', err);
      setSubmitError("Couldn't open your saved draft. Reload this page and try again — your draft is still saved.");
      return;
    }

    if (draft?.mediaUnavailable) {
      setSubmitError("Your draft's photos or recording couldn't be loaded. They're saved — try again when you have a stronger connection.");
      return;
    }

    // Local media is permanently gone (device evicted it) — retrying can
    // never recover it, so don't dead-end on an error. Open the capture
    // screen with the surviving notes/text and say what happened; the status
    // strip renders over the capture screen, so the message stays visible.
    if (draft?.mediaLost) {
      setSubmitError("Your draft's photos or recording couldn't be recovered from this device, but your notes are here — you can re-record and continue.");
      draft = { ...draft, blobs: [], durations: [] };
    }

    if (draft) {
      // Restore redo/addendum lineage if the draft was saved during a redo
      if (draft.lineage) {
        lineageRef.current = draft.lineage;
      }
      const initialDraft: InitialDraft = {
        notes: draft.notes,
        textContent: draft.textContent,
        mediaBlobs: draft.blobs,
        mediaDurations: draft.durations,
      };
      setPage({ screen: 'capture', task, captureType: draft.captureType, initialDraft });
    } else {
      // Draft missing or corrupted — remove stale entry and start fresh
      setDraftTaskIds(prev => { const n = new Set(prev); n.delete(task.id); return n; });
      handleStartTask(task);
    }
  };

  /* ── Toggle carousel ↔ grid view ── */
  const handleToggleViewMode = useCallback(() => {
    taskListRef.current?.captureRects();
    setViewMode(m => m === 'carousel' ? 'grid' : 'carousel');
  }, []);

  /* ── Logout → sign out + back to auth ── */
  const handleLogout = async () => {
    setIsProfileSheetOpen(false);
    await logout();
    setPage({ screen: 'auth' });
  };


  /* ── Render ── */

  // While Firebase is resolving auth state or joining a project, show nothing.
  // Only gate on isLoadingData for the initial load — once the user is past auth,
  // don't regress to a blank shell on pull-to-refresh / background data reloads.
  const pastAuth = page.screen !== 'auth';

  // Admin-preview escape hatch. Defined before every early return below: the
  // new participant UI replaces the researcher Header entirely, so this is the
  // ONLY way back to the dashboard. It was previously added per screen and so
  // only reached the guided + tasks shells, stranding a previewing researcher
  // on the auth, loading and capture screens.
  const adminRoleSwitcher = isAdminPreview ? (
    <RoleSwitcher
      userRole="participant"
      setUserRole={(role) => {
        if (role !== 'participant') {
          document.getElementById('root')?.classList.add('view-slide-out-right');
          setTimeout(() => {
            navigate('/dashboard');
            document.getElementById('root')?.classList.remove('view-slide-out-right');
          }, 300);
        }
      }}
    />
  ) : null;

  // The preview banner is fixed chrome: without reserving its 26px, it sits
  // on top of the app — the shell's first content and the sticky header dock
  // (top: 0) both slid under it. Published as a CSS var so .pnp-shell pads
  // for it and .sab-sticky pins below it; 0px (the fallback) everywhere else.
  useEffect(() => {
    if (!isAdminPreview) return;
    document.documentElement.style.setProperty('--preview-inset', '26px');
    return () => document.documentElement.style.removeProperty('--preview-inset');
  }, [isAdminPreview]);

  // Connectivity / submit-failure strip. Fixed so it reaches every screen
  // including the full-viewport capture and guided flows. submitError wins:
  // it is actionable, offline is ambient. Sits below the preview banner.
  const statusStrip = (submitError || isOffline) ? (
    <div
      role="status"
      onClick={submitError ? () => setSubmitError(null) : undefined}
      style={{
        position: 'fixed',
        top: adminRoleSwitcher ? '26px' : 0,
        left: 0,
        right: 0,
        zIndex: 1002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        padding: 'calc(env(safe-area-inset-top, 0px) + 7px) 14px 7px',
        background: submitError ? 'var(--ember)' : 'var(--ink)',
        color: '#fff',
        font: '500 12px/1.35 "Helvetica Neue", Arial, sans-serif',
        letterSpacing: '0.01em',
        textAlign: 'center',
        cursor: submitError ? 'pointer' : 'default',
      }}
    >
      {!submitError && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0 }}>
          <path d="M2 2l20 20" />
          <path d="M8.5 16.4a5 5 0 0 1 7 0" />
          <path d="M5 12.9a10 10 0 0 1 3.2-2.1" />
          <path d="M15.8 10.8A10 10 0 0 1 19 12.9" />
          <path d="M1.4 9.4a15 15 0 0 1 5-3.2" />
          <path d="M17.6 6.2a15 15 0 0 1 5 3.2" />
          <path d="M12 20h.01" />
        </svg>
      )}
      <span>{submitError || "You're offline — your work is saved on this device and will upload when you reconnect."}</span>
      {/* Visible dismiss affordance — the whole strip is tappable, but
          nothing said so, and on phones the strip covers the app bar. */}
      {submitError && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.85 }}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      )}
    </div>
  ) : null;

  // Fixed-overlay chrome, for the screens that own the whole viewport.
  // The preview banner is not decoration: preview discards everything on
  // submit, and without saying so a researcher submits, goes looking for the
  // response in their data, and reasonably concludes the product is broken.
  // statusStrip rides along here so it reaches all ~14 return sites below
  // without threading a second prop through every one of them.
  const adminOverlay = (
    <>
      {adminRoleSwitcher ? (
        <>
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001,
            background: '#1a1814', color: '#fff', textAlign: 'center',
            font: '500 12px/1 -apple-system, BlinkMacSystemFont, sans-serif',
            letterSpacing: '0.02em', padding: '7px 12px',
          }}>
            Preview mode — nothing you submit here is saved
          </div>
          <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 40px)', right: '12px', zIndex: 1000 }}>
            {adminRoleSwitcher}
          </div>
        </>
      ) : null}
      {statusStrip}
      {showLeaveNudge && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label="Unsaved work"
          style={{
            position: 'fixed', inset: 0, zIndex: 1003,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(26,24,20,0.55)', padding: '24px',
          }}
        >
          <div style={{
            background: 'var(--surface, #fff)', borderRadius: '20px',
            padding: '24px 20px', maxWidth: '320px', width: '100%',
            textAlign: 'center', color: 'var(--ink, #1a1814)',
            font: '400 14px/1.5 -apple-system, BlinkMacSystemFont, sans-serif',
            boxShadow: '0 24px 60px -20px rgba(26,24,20,0.45)',
          }}>
            <p style={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.3, margin: '0 0 8px' }}>
              Save your work as a draft?
            </p>
            <p style={{ margin: '0 0 18px', opacity: 0.75 }}>
              You have unsaved work on this task. Go back and tap
              {' '}<strong>Save draft</strong> to keep it for later.
            </p>
            <button
              onClick={() => setShowLeaveNudge(false)}
              style={{
                display: 'block', width: '100%', padding: '12px', marginBottom: '8px',
                borderRadius: '999px', border: 'none', cursor: 'pointer',
                background: 'var(--ink, #1a1814)', color: '#fff',
                fontFamily: 'inherit', fontWeight: 600, fontSize: '14px',
              }}
            >
              Go back
            </button>
            <button
              onClick={() => { setUnsavedCapture(false); performCaptureClose(); }}
              style={{
                display: 'block', width: '100%', padding: '12px',
                borderRadius: '999px', border: 'none', cursor: 'pointer',
                background: 'transparent', color: 'var(--ink, #1a1814)', opacity: 0.6,
                fontFamily: 'inherit', fontWeight: 500, fontSize: '14px',
              }}
            >
              Leave without saving
            </button>
          </div>
        </div>
      )}
    </>
  );

  // While Firebase is resolving, show branded loading.
  // Only gate on isLoadingData when we have claims (otherwise AppDataContext
  // never finishes loading because it waits for claimsRole).
  if (loading || joiningProject || isResettingOnboarding || (user && claimsRole && isLoadingData && !pastAuth)) {
    return <>{adminOverlay}<LoadingShell /></>;
  }

  if (page.screen === 'auth') {
    // If the user will be auto-forwarded to tasks by the useEffect,
    // show loading now to prevent the auth screen from flashing.
    // Only when ALL conditions for the tasks transition are met or about to be met.
    // Profile-complete, provisioned user heading to the home shell —
    // hold the branded loader until project data settles. Without the
    // `isLoadingData || !currentProject` arm, the frames where tasks
    // arrived (WARM) but projects (COLD) haven't render the AuthFlow
    // welcome screen before the auth→home effect fires (the welcome
    // flash on refresh/login). Bounded by AppDataContext's isLoadingData
    // fallback, so a genuinely zero-task project still proceeds.
    if (!forceOnboarding && user && userProfile?.profileComplete && projectId &&
        (tasks.length > 0 || isLoadingData || (!currentProject && !projectResolveTimedOut))) {
      return <>{adminOverlay}<LoadingShell /></>;
    }

    // Screen selection is delegated to the pure oracle so an indeterminate
    // state can never render a terminal guard screen. See
    // hooks/participantScreen.ts and tests/participantScreen.test.ts.
    const pendingJoinId = safeLocalStorage.getItem('pendingProjectJoin');
    const screen = deriveParticipantScreen({
      authResolved,
      hasUser: !!user,
      claimsKnown,
      claimsRole,
      profileSettled,
      dataReady: isDataReady(isLoadingData, appData !== null, claimsRole),
      // Only a join for a project the user is NOT already in counts as pending.
      pendingJoin: !!pendingJoinId && pendingJoinId !== projectId,
      joinInFlight: joiningProject,
      joinFailed,
      hasResolvedProject: !!projectId && !!currentProject,
      // No projectId at all = nothing to wait for, settled immediately (that
      // IS the "not assigned yet" case). With one, wait for the 7s bound.
      projectSettled: !projectId || projectResolveTimedOut,
      profileComplete: !!userProfile?.profileComplete,
    });

    if (!forceOnboarding && user) {
      if (isAdminPreview) return <>{adminOverlay}<LoadingShell /></>;
      if (screen === 'loading') return <>{adminOverlay}<LoadingShell /></>;
      if (screen === 'unprovisioned') {
        return <>{adminOverlay}<UnprovisionedScreen onLogout={handleLogout} /></>;
      }
      if (screen === 'waiting') {
        return (
          <>
            {adminOverlay}
            <WaitingScreen userEmail={user.email || undefined} onLogout={handleLogout} />
          </>
        );
      }
      // Drop a stale pending-join flag once the user is in that project.
      if (pendingJoinId && projectId === pendingJoinId) {
        safeLocalStorage.removeItem('pendingProjectJoin');
        safeLocalStorage.removeItem('pendingJoinRole');
      }
    }

    // 'login' | 'join' | 'profile' | 'welcome' map 1:1 onto AuthFlow steps.
    const initialStep: AuthStep =
      !user || forceOnboarding ? 'login' : (screen as AuthStep);

    const durationDays = currentProject?.startDate && currentProject?.endDate
      ? Math.max(
          1,
          Math.round(
            (new Date(currentProject.endDate).getTime() -
              new Date(currentProject.startDate).getTime()) /
              86400000
          ) + 1
        )
      : undefined;

    return (
      <>
      {adminOverlay}
      <AuthFlow
        initialStep={initialStep}
        onComplete={handleAuthComplete}
        projectId={projectId}
        projectName={currentProject?.name || 'Research Project'}
        organiserName={currentProject?.client || 'Research Team'}
        participantEmail={user?.email || undefined}
        participantName={user?.displayName || undefined}
        taskCount={tasks.length || undefined}
        durationDays={durationDays}
        welcomeTitle={currentProject?.welcomeTitle || undefined}
        welcomeMessage={currentProject?.welcomeMessage || undefined}
      />
      </>
    );
  }

  if (page.screen === 'guided') {
    // Derive first name the same way the regular hero does so the
    // ceremony screens feel like a natural continuation.
    const guidedFirstName = (profileData?.firstName || userProfile?.displayName || user?.displayName || '')
      .split(' ')[0]
      .trim();
    return (
      <>
      {/* Outside .pnp-shell — see the note on the tasks shell: the shell's
          transform animation would otherwise capture this fixed overlay. */}
      {adminOverlay}
      <div className="pnp-shell">
        {isVoiceGuided ? (
          <VoiceGuidedShell
            task={voiceGuidedTask}
            done={voiceGuidedDone}
            total={voiceGuidedEligibleTasks.length}
            voicePackLanguage={currentProject?.voicePackLanguage}
            voicePhraseUrls={currentProject?.voicePhraseUrls}
            onAnswer={handleVoiceGuidedAnswer}
          />
        ) : (
          <GuidedHomeShell
            step={page.step}
            firstName={guidedFirstName}
            done={submittedTaskIds.size}
            total={tasks.length}
            nextTask={nextIncompleteTask}
            researcherQuote={currentProject?.welcomeMessage || undefined}
            onBegin={handleGuidedAdvance}
            onAdvance={handleGuidedAdvance}
            onStartTask={handleGuidedStartTask}
            onBackFromBrief={handleGuidedBackFromBrief}
          />
        )}
        {/* No ParticipantInbox in guided mode — a one_time study is
            fire-and-forget; the messages/nudges surface is intentionally
            out of this experience. Delivery status is the UploadBar's
            job. */}

        {/* Upload progress indicator — must render on every guided
            ceremony screen so the participant can see the previous
            task's upload completing while they're on rest / brief /
            complete. */}
        <UploadBar />
      </div>
      </>
    );
  }

  if (page.screen === 'capture') {
    const { task, captureType, initialDraft } = page;
    const commonProps = {
      taskName: task.title || task.prompt,
      prompt: task.prompt,
      onClose: handleCaptureClose,
      onSubmit: makeSubmitHandler(task, captureType) as any,
      onSaveDraft: makeDraftHandler(task, captureType) as any,
    };

    /** Drop any saved IndexedDB draft for this task. Used by AudioCapture's
        discard-on-exit logic: if the user modified clips this session
        (deleted or recorded new) and closed without an explicit Save Draft
        or Submit, persistence is wiped so the card stops saying "Resume". */
    const handleDiscardDraft = async () => {
      try {
        await deleteDraft(task.id);
      } catch { /* IndexedDB write may race during navigation — ignore. */ }
      setDraftTaskIds(prev => {
        if (!prev.has(task.id)) return prev;
        const n = new Set(prev);
        n.delete(task.id);
        return n;
      });
    };

    const captureElement = (() => {
      switch (captureType) {
        case 'audio':  return <AudioCapture {...commonProps} initialDraft={initialDraft} onDiscardDraft={handleDiscardDraft} />;
        case 'photo':  return <PhotoCapture {...commonProps} initialDraft={initialDraft} photoConfig={task.photoConfig} />;
        case 'video':  return <VideoCapture {...commonProps} initialDraft={initialDraft} />;
        case 'text':   return <TextCapture {...commonProps} initialDraft={initialDraft} />;
        case 'screen': return <ScreenCapture {...commonProps} initialDraft={initialDraft} targetUrl={task.screenDeepLinkUrl} taskId={task.id} />;
        case 'guided': return <GuidedCapture {...commonProps} initialDraft={initialDraft} targetUrl={task.screenDeepLinkUrl} taskId={task.id} />;
        case 'signification': {
          const journalEntryNumber = task.significationConfig?.collectionMode === 'journal'
            ? userSubmissions.filter(s => s.taskId === task.id).length + 1
            : 1;
          return <SignificationCapture {...commonProps} task={task} journalEntryNumber={journalEntryNumber} />;
        }
        default:       return <AudioCapture {...commonProps} />;
      }
    })();

    return (
      <div
        ref={captureWrapRef}
        className={`pnp-fullbleed${isGuidedMode ? ' gd-capture-wrap' : ''}`}
        style={{ position: 'relative' }}
      >
        <CaptureErrorBoundary taskName={task.title || task.prompt} onClose={handleCaptureClose}>
          {captureElement}
        </CaptureErrorBoundary>
        {isGuidedMode && (
          <GuidedHeaderBand done={submittedTaskIds.size} total={tasks.length} />
        )}
        {adminOverlay}
      </div>
    );
  }

  // Still loading initial data — keep showing branded splash instead of empty task view.
  // Gate on dataSettled AND require that appData.tasks has been checked for this project.
  // Without the second check, there's a 1-frame flash of "0 tasks" while memos catch up.
  const dataSettled = !!appData && !!projectId && !isLoadingData;
  const projectHasTasks = appData?.tasks.some(t => t.projectId === projectId && t.status === 'active') ?? false;
  // A researcher previewing with no project selected has nothing to resolve —
  // dataSettled can never become true, so the loader below would spin forever.
  // Say so and leave the toggle as the way out.
  if (isAdminPreview && !projectId) {
    return (
      <>
        {adminOverlay}
        <div className="pnp-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center' }}>
          <p style={{ maxWidth: 280, opacity: 0.75 }}>
            Select a project first — then switch back to the participant view to preview it.
          </p>
        </div>
      </>
    );
  }

  if (tasks.length === 0 && (!dataSettled || projectHasTasks)) {
    return <>{adminOverlay}<LoadingShell /></>;
  }

  // Tasks screen
  const displayName = profileData?.firstName || userProfile?.displayName || user?.displayName || 'Participant';
  // Only use avatars explicitly set during profile setup — never fall back to
  // Google/provider photoURL so participants must take their own picture.
  const displayAvatar = avatarUrl || userProfile?.avatarUrl || undefined;

  return (
    <>
    {/* Rendered OUTSIDE .pnp-shell on purpose. The shell animates with
        transform (pnpSlideIn), and a transformed ancestor becomes the
        containing block for position:fixed children — so nested here the
        toggle anchored to the phone column mid-animation and visibly jumped
        to the viewport edge when the transform dropped. */}
    {adminOverlay}
    <div className="pnp-shell">
      <div className="pnp-aurora" aria-hidden="true" />

      {/* Status pill — tappable avatar + project name + ACTIVE/X-of-N + progress-ring toggle */}
      <div className="pnp-status-bar" onClick={() => setIsProfileSheetOpen(true)}>
        <div className="pnp-sb-avatar">
          {displayAvatar ? (
            <img src={displayAvatar} alt={displayName} className="pnp-sb-avatar-img" />
          ) : (
            <span className="pnp-sb-avatar-initial">{displayName.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="pnp-sb-info">
          <span className="pnp-sb-project">{currentProject?.name || 'Study'}</span>
          <span className="pnp-sb-meta-rs">
            {studyTimeline ? `Day ${studyTimeline.dayNumber} of ${studyTimeline.totalDays}` : 'Active'}
          </span>
        </div>
        {/* Inbox shortcut — opens the messages sheet from anywhere on home.
            Always visible so the user can re-read recent nudges even after
            acknowledging them. Ember dot when there's an unread nudge or
            thread reply. */}
        <button
          className={`pnp-inbox-toggle ${totalUnreadCount > 0 ? 'pnp-inbox-toggle--has-unread' : ''}`}
          onClick={(e) => { e.stopPropagation(); setInboxOpen(true); }}
          aria-label={totalUnreadCount > 0 ? `Open messages — ${totalUnreadCount} unread` : 'Open messages'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
        {/* Grid / carousel view toggle. Dark-mode toggle parked. */}
        <button
          className={`pnp-view-toggle ${viewMode === 'grid' ? 'pnp-view-toggle--active' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleToggleViewMode(); }}
          aria-label={viewMode === 'carousel' ? 'Switch to grid view' : 'Switch to carousel view'}
        >
            {viewMode === 'carousel' ? (
              /* Grid icon — 2x2 outlined squares */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="8" height="8" rx="1.5" />
                <rect x="13" y="3" width="8" height="8" rx="1.5" />
                <rect x="3" y="13" width="8" height="8" rx="1.5" />
                <rect x="13" y="13" width="8" height="8" rx="1.5" />
              </svg>
            ) : (
              /* Stack icon — 3 stacked outlined rects */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="3" width="16" height="14" rx="2" opacity="0.4" />
                <rect x="3" y="6" width="18" height="14" rx="2" opacity="0.65" />
                <rect x="2" y="9" width="20" height="13" rx="2" />
              </svg>
            )}
        </button>
      </div>

      {/* Greeting block — eyebrow date, big greeting title, stats/signal row.
          Stats row uses a priority cascade (Option B + D):
            1. Researcher signal (unread nudge → recent presence → past presence)
            2. Personal momentum (streak + days-to-go)
            3. Calm welcome floor (never empty, even on day 1) */}
      {(() => {
        const hour = new Date().getHours();
        const partOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
        const firstName = (profileData?.firstName || userProfile?.displayName || displayName || '').split(' ')[0];
        const dateLabel = (() => {
          const d = new Date();
          const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
          const month = d.toLocaleDateString('en-US', { month: 'short' });
          const day = d.getDate();
          return `${weekday} · ${month} ${day}`.toUpperCase();
        })();
        const daysRemaining = studyTimeline ? Math.max(0, studyTimeline.totalDays - studyTimeline.dayNumber) : null;
        const showStreak = streak >= 2;

        // Priority cascade for the signal row.
        type HeroSignal = { kind: 'nudge' | 'presence-active' | 'presence-recent'; text: string };
        const heroSignal: HeroSignal | null = (() => {
          // Hero pill — never gate on nudgeSenderName since researcher
          // team-member docs may pre-date the userId field. If sender
          // can't be resolved, fall back to a generic phrasing so the
          // user still sees the indicator.
          if (activeHeroNudge) {
            const senderFirst = nudgeSenderName ? nudgeSenderName.split(' ')[0] : 'Researcher';
            return {
              kind: 'nudge',
              text: nudgeTaskName
                ? `${senderFirst} nudged you about "${nudgeTaskName}"`
                : `${senderFirst} sent you a message`,
            };
          }
          if (researcherPresence) {
            return researcherPresence.recent
              ? { kind: 'presence-active', text: `${researcherPresence.name} is here now` }
              : { kind: 'presence-recent', text: `${researcherPresence.name} was here recently` };
          }
          return null;
        })();

        return (
          <div className="pnp-rs-greeting" data-view-mode={viewMode}>
            <span className="pnp-rs-greeting-eyebrow">{dateLabel}</span>
            <h1 className="pnp-rs-greeting-title">
              {firstName?.trim()
                ? <>Good {partOfDay},<br/><em><span className="name">{firstName.trim()}</span></em><span className="period">.</span></>
                : <>Good {partOfDay}.</>}
            </h1>
            <div className="pnp-rs-stats">
              {heroSignal ? (
                heroSignal.kind === 'nudge' ? (
                  // Nudge variant — tappable, opens the inbox so user can
                  // read the full message and act. Presence variants stay
                  // as plain spans (informational, no action). The
                  // --arrival modifier triggers a one-shot bounce when a
                  // genuinely-new nudge appears this session; replaces
                  // the old NudgeToast attention-grabber.
                  <button
                    type="button"
                    className={`pnp-rs-stat pnp-rs-stat-signal pnp-rs-stat-signal--nudge pnp-rs-stat-signal--tappable${animatingNudgeId === activeHeroNudge?.id ? ' pnp-rs-stat-signal--arrival' : ''}`}
                    aria-label={`${heroSignal.text} — tap to open messages`}
                    onClick={() => setInboxOpen(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span>{heroSignal.text}</span>
                  </button>
                ) : (
                  <span className={`pnp-rs-stat pnp-rs-stat-signal pnp-rs-stat-signal--${heroSignal.kind}`} aria-label={heroSignal.text}>
                    <span className="pnp-rs-presence-dot" aria-hidden="true" />
                    <span>{heroSignal.text}</span>
                  </span>
                )
              ) : (showStreak || daysRemaining !== null) ? (
                <>
                  {showStreak && (
                    <span className="pnp-rs-stat" aria-label={`${streak} day streak`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2s4 4 4 8a4 4 0 11-8 0c0-3 2-4 2-6"/><path d="M8 14a6 6 0 0012 0"/></svg>
                      <span>{streak} day streak</span>
                    </span>
                  )}
                  {showStreak && daysRemaining !== null && <span className="pnp-rs-stat-divider" aria-hidden="true" />}
                  {daysRemaining !== null && (
                    <span className="pnp-rs-stat" aria-label={`${daysRemaining} days to go`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      <span>{daysRemaining} to go</span>
                    </span>
                  )}
                </>
              ) : (
                /* Floor: day 1 / no streak / no timeline. Never empty. */
                <span className="pnp-rs-stat pnp-rs-stat-welcome">
                  <span>Ready when you are</span>
                </span>
              )}
            </div>
          </div>
        );
      })()}

      <TaskList
        ref={taskListRef}
        tasks={tasks}
        taskStates={taskStates}
        activeTaskId={activeTaskId}
        onSelectTask={handleSelectTask}
        onStartTask={handleStartTask}
        onResumeTask={handleResumeTask}
        resumingTaskId={resumingTaskId}
        onRedoTask={handleRedo}
        submissions={userSubmissions}
        threadsBySubmission={threadsBySubmission}
        unreadCounts={threadUnreadCounts}
        threadContext={threadContext}
        onAddendum={handleAddendum}
        onSubmitSequenceDone={() => { setRecentlySubmittedTaskId(null); setSubmittedAt(undefined); }}
        milestone={milestone}
        submittedAt={submittedAt}
        unreadMessageCount={totalUnreadCount}
        redoCount={redoCount}
        onViewMessages={() => setInboxOpen(true)}
        scrollToSubmissionId={scrollToSubmissionId}
        viewMode={viewMode}
        onToggleViewMode={handleToggleViewMode}
      />

      <ProfileSheet
        isOpen={isProfileSheetOpen}
        onClose={() => setIsProfileSheetOpen(false)}
        user={{ name: displayName, avatarUrl: displayAvatar }}
        progress={{ completed: completedCount, total: tasks.length }}
        onLogout={handleLogout}
        onAvatarChange={handleAvatarChange}
      />

      {formatSelectTask && (
        <FormatSheet
          mediaTypes={formatSelectTask.mediaTypes}
          taskName={formatSelectTask.prompt}
          onSelect={handleFormatSelect}
          onClose={() => setFormatSelectTask(null)}
        />
      )}

      <ParticipantInbox
        isOpen={inboxOpen}
        onClose={() => setInboxOpen(false)}
        threads={inboxThreads}
        submissions={userSubmissions}
        tasks={tasks}
        threadsBySubmission={threadsBySubmission}
        threadContext={threadContext}
        nudges={unreadNudgesForMe}
        recentReadNudges={recentReadNudgesForMe}
        teamMembers={appData?.teamMembers}
        onMarkNudgeAsRead={(nudgeId) => {
          markNudgeAsRead(nudgeId).catch(() => { /* swallow — snapshot will retry */ });
        }}
        onNavigateToTask={handleNudgeNavigateToTask}
        onNavigateToSubmission={(subId) => {
          setScrollToSubmissionId(subId);
          // Clear after a tick so it can be re-triggered
          setTimeout(() => setScrollToSubmissionId(null), 1000);
        }}
      />

      <UploadBar />
    </div>
    </>
  );
};
